import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { appPath } from '../../../utils/appPath'

const DOT_COLORS = ['text-blue-400', 'text-violet-400', 'text-amber-500'] as const

export interface EmployeeDetailHeaderBarProps {
  title: string
  /** Active / Inactive — same slot as project category pill. */
  statusLabel: 'Active' | 'Inactive'
  email?: string | null
  username?: string | null
  userId: number
  statusPill?: string
  periodSlot: React.ReactNode
}

export function EmployeeDetailHeaderBar({
  title,
  statusLabel,
  email,
  username,
  userId,
  statusPill,
  periodSlot,
}: EmployeeDetailHeaderBarProps) {
  const metaSegments = useMemo(() => {
    const out: { key: string; label: string; value: string }[] = []
    if (email?.trim())
      out.push({ key: 'email', label: 'Email', value: email.trim() })
    if (username?.trim())
      out.push({
        key: 'user',
        label: 'Username',
        value: `@${username.trim()}`,
      })
    out.push({
      key: 'id',
      label: 'User ID',
      value: String(userId),
    })
    return out
  }, [email, username, userId])

  const statusIsActive = statusLabel === 'Active'

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3 lg:flex-row lg:items-center lg:justify-between w-full min-w-0">
      <div className="flex gap-2.5 sm:gap-3 min-w-0 flex-1">
        <Link
          to={appPath('/employee-analysis')}
          className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-white hover:text-slate-900 transition-colors flex-none self-center shrink-0 bg-white/80"
        >
          <ArrowLeft size={13} /> Back
        </Link>

        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h1 className="text-base sm:text-[17px] font-bold text-[#0D0D0D] leading-snug">
              {title}
            </h1>
            <span
              className={
                statusIsActive
                  ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide shrink-0'
                  : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide shrink-0'
              }
            >
              {statusLabel}
            </span>
          </div>

          {metaSegments.length > 0 ? (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-1.5 text-[11px] sm:text-[12px]">
              {metaSegments.map((seg, i) => (
                <React.Fragment key={seg.key}>
                  {i > 0 ? (
                    <span
                      className={`${DOT_COLORS[(i - 1) % DOT_COLORS.length]} select-none text-sm leading-none`}
                      aria-hidden
                    >
                      •
                    </span>
                  ) : null}
                  <span className="text-slate-600 min-w-0">
                    <span className="text-slate-400 font-medium">
                      {seg.label}
                    </span>{' '}
                    <span className="text-slate-800 font-medium truncate inline align-baseline max-w-[min(14rem,40vw)]">
                      {seg.value}
                    </span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          ) : null}

          {statusPill ? (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span className="bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide">
                {statusPill}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-none w-full lg:w-auto lg:max-w-[min(36rem,48vw)] xl:max-w-2xl shrink-0">
        {periodSlot}
      </div>
    </div>
  )
}
